import { injectable, decorate } from "inversify";
import { BaseAuthService, AuthResponse, AuthResponseWithData, IAccountInfo } from "sensei-authentication";
import { IUser } from "csd.phoenix.models/User";
import ITenantInfo from "../../models/TenantInfo";
import { getCurrentUserInfoFromList, getUsers } from "../../stores/usersStore";
import { saveCurrentTenantSelection } from "./storage";
import { useInject } from "../../di";
import { IAuthRootStore, iid_authRootStore } from "./AuthRootStore";

// inversify 5.x requires every class in the prototype chain to carry @injectable(),
// including third-party base classes. BaseAuthService (from sensei-authentication)
// isn't decorated, so it's decorated here externally rather than at its declaration.
decorate(injectable(), BaseAuthService);

@injectable()
export class AuthService extends BaseAuthService<IUser> {
  private rootStore = useInject<IAuthRootStore>(iid_authRootStore);

  defaultTenant = undefined;

  handleAuthenticatedAccount = (_: IAccountInfo, tenant?: unknown): AuthResponse => {
    if (tenant) saveCurrentTenantSelection(tenant as ITenantInfo);
    return { success: true };
  };

  loadUser = async (accountInfo: IAccountInfo): Promise<AuthResponseWithData<IUser | undefined>> => {
    try {
      const users = await getUsers();
      const user = getCurrentUserInfoFromList(users, accountInfo.email);
      return { success: true, value: user };
    } catch {
      return { success: false, status: "UserHasNoAccess" };
    }
  };

  onAuthenticated = async (user: IUser) => {
    this.rootStore.setUser(user);
  };

  // MFA enforcement is deferred: Forms has no configOverride wired up to swap
  // the B2C login policy to an MFA-capable one, so the checkMFA/second-login
  // flow this flag triggers has no real effect. PO decided to hold off on
  // Forms-specific MFA until a later time. 
  requiresMFA = async () => {
    return false;
  };
}
